/**
 * Cookie management module
 * Provides utilities for setting, getting, and deleting browser cookies
 */

import { CONFIG } from '../core/config.js';

/**
 * Sets a cookie with the specified name, value, and expiration
 *
 * @function setCookie
 * @param {string} name - The name of the cookie
 * @param {string|boolean|number} value - The value to store in the cookie
 * @param {number} [days] - Number of days until the cookie expires (default: CONFIG.COOKIES.DEFAULT_EXPIRY_DAYS)
 *
 * @description Creates a new cookie or updates an existing one. The cookie will be
 * available across the entire domain (path=/). If no expiration is specified,
 * the cookie will expire after the default number of days from config.
 *
 * @example
 * setCookie('username', 'john_doe', 30); // Expires in 30 days
 * setCookie('theme', 'dark'); // Expires in default days
 * setCookie('sidebarCollapsed', true, 7); // Boolean value, expires in 7 days
 */
export function setCookie(name, value, days = CONFIG.COOKIES.DEFAULT_EXPIRY_DAYS) {
    let expires = '';
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = `; expires=${date.toUTCString()}`;
    }
    document.cookie = `${name}=${value || ''}${expires}; path=/`;
}

/**
 * Retrieves the value of a cookie by its name
 *
 * @function getCookie
 * @param {string} name - The name of the cookie to retrieve
 * @returns {string|null} The cookie value if found, null if the cookie doesn't exist
 *
 * @description Searches through all available cookies and returns the value
 * of the specified cookie. Returns null if the cookie is not found.
 * Handles whitespace trimming and proper cookie parsing.
 *
 * @example
 * const username = getCookie('username');
 * if (username) {
 *     console.log('Welcome back, ' + username);
 * }
 */
export function getCookie(name) {
    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
}

/**
 * Deletes a cookie by setting its expiration date to the past
 *
 * @function deleteCookie
 * @param {string} name - The name of the cookie to delete
 *
 * @description Removes a cookie by setting its expiration date to January 1, 1970,
 * which causes the browser to immediately delete it. The cookie is removed
 * from the entire domain (path=/).
 *
 * @example
 * deleteCookie('oldPreference'); // Removes the 'oldPreference' cookie
 * deleteCookie('sessionToken'); // Removes the 'sessionToken' cookie
 */
export function deleteCookie(name) {
    document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
}
